<ng-container *ngIf="active">
  <div class="wf-empty" *ngIf="!steps.length">
    No steps yet - add a Start step to anchor the bridge, then Change/Total steps to build it out.
  </div>

  <div class="wf-step-card" *ngFor="let step of steps; let i = index; let first = first; let last = last">
    <div class="wf-step-head">
      <span class="wf-step-index">{{ i + 1 }}</span>

      <ng-select
        #ssWfStepType
        class="ss-select wf-step-type-select"
        [class.is-open]="!!ssWfStepType.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="stepTypes"
        bindLabel="label"
        bindValue="value"
        [clearable]="true"
        [ngModel]="step.stepType"
        (ngModelChange)="step.stepType = $event ?? ''; onStepTypeChange(step)"
      ></ng-select>

      <input
        type="text"
        class="wf-step-label"
        [ngModel]="step.label"
        (ngModelChange)="step.label = $event; onStepFormulaOrLabelChange()"
        placeholder="Display label"
      />


      <div class="wf-step-actions">
        <button type="button" mat-icon-button [disabled]="first" (click)="moveStepUp(i)" matTooltip="Move up" aria-label="Move step up">
          <mat-icon>arrow_upward</mat-icon>
        </button>
        <button type="button" mat-icon-button [disabled]="last" (click)="moveStepDown(i)" matTooltip="Move down" aria-label="Move step down">
          <mat-icon>arrow_downward</mat-icon>
        </button>
        <button type="button" mat-icon-button class="row-remove-button" (click)="removeStep(step)" matTooltip="Remove step" aria-label="Remove step">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <div class="wf-step-mode-toggle" *ngIf="step.stepType === 'CHANGE' || step.stepType === 'START'" role="group" aria-label="Step input mode">
      <button type="button" [class.is-active]="!step.formulaMode" (click)="step.formulaMode ? toggleStepFormulaMode(step) : null">
        <mat-icon>table_chart</mat-icon>
        Dataset
      </button>
      <button type="button" [class.is-active]="step.formulaMode" (click)="!step.formulaMode ? toggleStepFormulaMode(step) : null">
        <mat-icon>functions</mat-icon>
        Formula
      </button>
    </div>

    <!-- Direction applies to a CHANGE step's own delta/bar regardless of
         whether its value comes from a dataset aggregation or a formula,
         so it's shown for both modes (moved out of the dataset-only body
         below, which used to hide it whenever Formula mode was active). -->
    <div class="wf-step-body" *ngIf="step.stepType === 'CHANGE' && host.fieldEnabled('steps', 'sign')">
      <div class="control-group">
        <label>{{ host.fieldLabel('steps', 'sign', 'Direction') }}</label>
        <ng-select
          #ssWfSignShared
          class="ss-select"
          [class.is-open]="!!ssWfSignShared.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="signOptions"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [ngModel]="step.sign"
          (ngModelChange)="step.sign = $event ?? ''; onStepFormulaOrLabelChange()"
        ></ng-select>
      </div>
    </div>

    <div
      class="wf-step-body wf-field-drop-zone"
      *ngIf="step.stepType !== 'TOTAL' && !step.formulaMode"
      (dragover)="host.allowFieldDrop($event)"
      (drop)="onStepFieldDrop($event, step)"
      title="Drop a field from the Data pane"
    >
      <div class="control-group" *ngIf="host.fieldEnabled('steps', 'dataset')">
        <label>{{ host.fieldLabel('steps', 'dataset', 'Dataset') }}</label>
        <ng-select
          #ssWfDataset
          class="ss-select"
          [class.is-open]="!!ssWfDataset.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="host.entityOptions"
          [clearable]="true"
          [ngModel]="step.dataset"
          (ngModelChange)="onStepDatasetChange(step, $event)"
        ></ng-select>
      </div>
      <div class="control-group" *ngIf="host.fieldEnabled('steps', 'field')">
        <label>{{ host.fieldLabel('steps', 'field', 'Field') }}</label>
        <ng-select
          #ssWfField
          class="ss-select"
          [class.is-open]="!!ssWfField.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="columnsForStep(step)"
          [clearable]="true"
          [searchable]="true"
          [loading]="isStepColumnsLoading(step)"
          [placeholder]="stepFieldPlaceholder(step)"
          [ngModel]="step.field || null"
          (ngModelChange)="onStepFieldChange(step, $event)"
        ></ng-select>
      </div>
      <div class="control-group" *ngIf="host.fieldEnabled('steps', 'aggregation')">
        <label>{{ host.fieldLabel('steps', 'aggregation', 'Aggregation') }}</label>
        <ng-select
          #ssWfAgg
          class="ss-select"
          [class.is-open]="!!ssWfAgg.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="host.measureAggregations"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [ngModel]="step.aggregation"
          (ngModelChange)="step.aggregation = $event ?? ''; onStepFormulaOrLabelChange()"
        ></ng-select>
      </div>
    </div>

    <div class="wf-step-body" *ngIf="(step.stepType === 'TOTAL' || step.formulaMode) && host.fieldEnabled('steps', 'formula')">
      <div
        class="control-group control-group-full wf-field-drop-zone"
        (dragover)="host.allowFieldDrop($event)"
        (drop)="onWaterfallFormulaDrop(step, $event)"
        title="Drag a column here to add it to the formula"
      >
        <label>{{ host.fieldLabel('steps', 'formula', 'Calculation logic / formula') }}</label>
        <input
          type="text"
          [ngModel]="step.formula"
          (ngModelChange)="step.formula = $event; onStepFormulaOrLabelChange()"
          placeholder="Drag columns here, e.g. [Gross Revenue] + [Product Sales] - [COGS]"
        />
      </div>
    </div>
  </div>

  <!-- Per-axis label FONT WEIGHTS — waterfall's option builder is fully
       format-driven, so these write straight into the shared format bag
       (same keys buildWaterfallOption reads); '' = default (falls back
       to the Format accordion's tile-wide "Axis label weight").
       CONFIG-DRIVEN via the waterfallAxis layoutSteps section in
       charts/waterfall.json; options from the shell `fontWeights` list. -->
  <div class="control-row" *ngIf="host.fieldEnabled('waterfallAxis', 'xAxisFontWeight') || host.fieldEnabled('waterfallAxis', 'yAxisFontWeight')">
    <div class="control-group" *ngIf="host.fieldEnabled('waterfallAxis', 'xAxisFontWeight')">
      <label>{{ host.fieldLabel('waterfallAxis', 'xAxisFontWeight', 'X axis label weight') }}</label>
      <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="host.barFormat['xAxisFontWeight'] || ''" (ngModelChange)="host.barFormat['xAxisFontWeight'] = $event ?? ''; host.onBarFormatChange()"></ng-select>
    </div>
    <div class="control-group" *ngIf="host.fieldEnabled('waterfallAxis', 'yAxisFontWeight')">
      <label>{{ host.fieldLabel('waterfallAxis', 'yAxisFontWeight', 'Y axis label weight') }}</label>
      <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="host.barFormat['yAxisFontWeight'] || ''" (ngModelChange)="host.barFormat['yAxisFontWeight'] = $event ?? ''; host.onBarFormatChange()"></ng-select>
    </div>
  </div>

  <!-- Waterfall chart options — the SAME config-driven format accordions as
       the Bar layout (Title / Axis / Legend / Labels / Tooltip / Formatting /
       Advanced), fed by the WATERFALL-scoped formatControls in
       charts/waterfall.json and gated by the capabilityRegistry `sections`
       list; the values fold into the tile on Generate. -->
  <ng-container [ngTemplateOutlet]="chartFormatAccordion"></ng-container>
</ng-container>
